// The timesheet CSV between two dates (round 44): what the bookkeeper pays
// from. A plain link to the server, like the form PDF's download: the file is
// built there, behind the same sign-in, and named for its dates.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../session';
import { PageHeader } from '../components/ui/Page';
import { addDays, todayISO } from '../lib/dates';
import { weekStartFor } from '../lib/timesheetWeeks';
import { timesheets } from '../timesheetsApi';
import { DownloadLink } from '../components/DownloadLink';

export function TimesheetExportPage() {
  const { tenant } = useSession();
  const lastWeekStart = addDays(weekStartFor(todayISO(), tenant?.timesheetWeekStart ?? 1), -7);
  const [from, setFrom] = useState(lastWeekStart);
  const [to, setTo] = useState(addDays(lastWeekStart, 6));
  const [detail, setDetail] = useState<'days' | 'weeks'>('days');
  const [approvedOnly, setApprovedOnly] = useState(true);

  const valid = from !== '' && to !== '' && from <= to;

  return (
    <>
      <PageHeader
        title="Export timesheets"
        subtitle="A spreadsheet of the hours between two dates, for payroll."
        actions={
          <Link to="/app/timesheets" className="btn-secondary">
            All timesheets
          </Link>
        }
      />

      <section className="panel">
        <div className="row pair">
          <label htmlFor="export-from">
            From
            <input id="export-from" type="date" className="form-input" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label htmlFor="export-to">
            To
            <input id="export-to" type="date" className="form-input" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
        {!valid && <p className="mt-1 text-sm text-red-700">The to date has to be on or after the from date.</p>}

        <fieldset className="mt-4">
          <legend className="form-label">One row for each</legend>
          <label className="flex min-h-11 items-center gap-3 text-sm">
            <input type="radio" name="detail" className="h-5 w-5" checked={detail === 'days'} onChange={() => setDetail('days')} />
            Day: start, finish, break, job, leave and allowances, the way payroll is keyed in
          </label>
          <label className="flex min-h-11 items-center gap-3 text-sm">
            <input type="radio" name="detail" className="h-5 w-5" checked={detail === 'weeks'} onChange={() => setDetail('weeks')} />
            Week: each person’s totals, to check against
          </label>
        </fieldset>

        <label className="mt-2 flex min-h-11 items-center gap-3 text-sm">
          <input type="checkbox" className="h-5 w-5" checked={approvedOnly} onChange={(e) => setApprovedOnly(e.target.checked)} />
          Approved timesheets only
        </label>
        <p className="hint">
          {approvedOnly
            ? 'Only weeks a manager has approved.'
            : 'Submitted weeks too, with a Status column saying which is which. Drafts are never included.'}
        </p>

        <div className="mt-4">
          {valid ? (
            <DownloadLink className="btn-primary" href={timesheets.exportUrl({ from, to, detail, approvedOnly })}>
              Download CSV
            </DownloadLink>
          ) : (
            <button type="button" className="btn-primary" disabled>
              Download CSV
            </button>
          )}
        </div>
        <p className="hint !mb-0 mt-3">
          Opens in Excel, Numbers or Google Sheets. Dates are written 2026-10-06 so they sort properly, times are
          24-hour, and hours are decimal (7.50 is seven and a half hours). These are hours as recorded: no pay rates or
          award rules are applied.
        </p>
      </section>
    </>
  );
}
